import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

// Every mounted guard, so a way out that isn't a link (Log Out) can ask the
// one that is active
const guards = new Set();

/**
 * Leave the page through `proceed()`, after the active guard, if any, has
 * had its say (saved, or asked). For a way out that isn't a link, such as
 * signing out from the navigation bar; links are caught on their own.
 */
export function leaveThen(proceed) {
    const guard = [...guards].find((g) => g.current.active);
    if (!guard) return proceed();
    return guard.current.onLeaveAttempt(proceed);
}

/**
 * Stops someone leaving a page while `active`, and hands the attempt to
 * `onLeaveAttempt(proceed)` to decide: save first, or ask, then call
 * `proceed()` to go on with the leave, or don't to stay.
 *
 * Three ways out are covered. Closing or reloading the tab, or following a
 * link to another site, gets the browser's own "leave site?" prompt, the
 * only thing a page may do then. A click on a link to another page of this
 * app is caught before React Router sees it (capture phase on the document);
 * its proceed() navigates there. Anything else that leaves, such as Log Out,
 * calls leaveThen.
 *
 * The app's router is a BrowserRouter, not a data router, so React Router's
 * own useBlocker is unavailable and the Back button can't be held. Callers
 * cover Back by saving when they unmount (useSavedProgress does).
 */
export default function useLeaveGuard(active, onLeaveAttempt) {
    const location = useLocation();
    const navigate = useNavigate();
    const here = `${location.pathname}${location.search}`;
    const latest = useRef({ active, onLeaveAttempt, here, navigate });
    latest.current = { active, onLeaveAttempt, here, navigate };

    useEffect(() => {
        guards.add(latest);

        const onBeforeUnload = (event) => {
            if (!latest.current.active) return;
            event.preventDefault();
            // Chrome and Edge show the prompt only when returnValue is set
            event.returnValue = '';
        };

        const onClick = (event) => {
            if (!latest.current.active) return;
            if (
                event.defaultPrevented ||
                event.button !== 0 ||
                event.metaKey ||
                event.ctrlKey ||
                event.shiftKey ||
                event.altKey
            ) {
                return;
            }
            const anchor = event.target?.closest?.('a[href]');
            if (!anchor || anchor.hasAttribute('download')) return;
            // An in-page anchor, such as a dropdown toggle, goes nowhere
            if (anchor.getAttribute('href').startsWith('#')) return;
            if (anchor.target && anchor.target !== '_self') return;
            const url = new URL(anchor.href, window.location.href);
            // Another site: beforeunload covers it
            if (url.origin !== window.location.origin) return;
            // A link to the page already showing leaves nothing
            if (`${url.pathname}${url.search}` === latest.current.here) return;
            event.preventDefault();
            event.stopPropagation();
            const to = `${url.pathname}${url.search}${url.hash}`;
            latest.current.onLeaveAttempt(() => latest.current.navigate(to));
        };

        window.addEventListener('beforeunload', onBeforeUnload);
        document.addEventListener('click', onClick, true);
        return () => {
            guards.delete(latest);
            window.removeEventListener('beforeunload', onBeforeUnload);
            document.removeEventListener('click', onClick, true);
        };
    }, []);
}
